import { Component, OnInit } from '@angular/core';
import * as qs from 'query-string';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Product } from '../../../../../common/js/static/Product';
import { ModalType, ValidateDocumentAccess } from '../../../../../coverpath-common/js/interfaces/CoverpathTypes';
import { ModalService } from '../../../../../coverpath-common/angular/services/modal/modal.service';
import { AmorphicService } from '../../../../../coverpath-common/angular/services/amorphic/amorphic.service';
import { UICategory, UILoggerSingleton, UIPiiLevelEnum } from '../../../../../common/js/logger/UILoggerSingleton';

@Component({
  moduleId: module.id,
  selector: 'insured-esign',
  templateUrl: './insured-esign.component.html',
})
export class InsuredEsignComponent implements OnInit {
  controller;
  validateDocumentAccessData: ValidateDocumentAccess = {
    isValid: true,
    polno: '',
    signerFirstName: '',
    attemptsRemaining: 3,
    esignCompleted: false,
    urlExpired: false,
    recipient: '',
  };

  modalService: ModalService;
  validateDocumentAccessForm: FormGroup;
  loadingScreen: boolean = true;
  constructor(
    protected formBuilder: FormBuilder,
    protected amorphicService: AmorphicService,
    modalService: ModalService
  ) {
    this.controller = amorphicService.controller;
    this.modalService = modalService;
  }

  async ngOnInit() {
    this.validateDocumentAccessForm = this.formBuilder.group({
      SSN: [''],
      attemptsRemaining: [''],
    });
    const parsed = qs.parse(location.search);
    this.validateDocumentAccessData = await this.controller.validateInsuredEsignUrl(parsed.jwtToken);
    this.setAttemptsRemaining(this.validateDocumentAccessData.attemptsRemaining);
    this.loadingScreen = false;
  }

  protected setAttemptsRemaining(attemptsRemaining) {
    attemptsRemaining = Number(attemptsRemaining);
    this.validateDocumentAccessForm.get('attemptsRemaining').setValue(attemptsRemaining);
    if (attemptsRemaining === 0) {
      this.validateDocumentAccessForm.get('attemptsRemaining').setErrors({
        max: attemptsRemaining,
      });
    } else {
      this.validateDocumentAccessForm.get('attemptsRemaining').setErrors(null);
    }
  }

  async validateSSN() {
    this.loadingScreen = true;
    const { documents, attemptsRemaining } = await this.controller.validateInsuredSSNAndGetDocsToSign(
      this.validateDocumentAccessData.polno,
      this.validateDocumentAccessForm.get('SSN').value
    );
    this.setAttemptsRemaining(attemptsRemaining);

    if (documents && documents.length) {
      const modalConfig = {
        type: ModalType.esignModal,
        formsToSign: this.setupDocumentsToBeSigned(documents),
        onFinishSigning: async () => {
          const success = await this.controller.insuredEsignComplete(
            qs.parse(location.search).jwtToken,
            this.validateDocumentAccessData.polno
          );
          if (success) {
            this.validateDocumentAccessData.esignCompleted = true;
            UILoggerSingleton.getInstance().info(
              {
                module: 'InsuredEsignComponent',
                function: 'onFinishSigning',
                category: UICategory.request,
                context: {
                  piiLevel: UIPiiLevelEnum.nonPII,
                },
              },
              'Insured finished signing'
            );
          } else {
            this.validateDocumentAccessData.isValid = false;
            UILoggerSingleton.getInstance().info(
              {
                module: 'InsuredEsignComponent',
                function: 'onFinishSigning',
                category: UICategory.request,
                context: {
                  piiLevel: UIPiiLevelEnum.nonPII,
                },
              },
              'Insured did not complete eSign due to invalid token or policyNumber'
            );
          }
        },
      };
      await this.modalService.activate(modalConfig);
    } else {
      this.validateDocumentAccessForm.get('attemptsRemaining').setErrors({
        max: attemptsRemaining,
      });
      UILoggerSingleton.getInstance().info(
        {
          module: 'InsuredEsignComponent',
          function: 'validateSSN',
          category: UICategory.request,
          context: {
            piiLevel: UIPiiLevelEnum.nonPII,
          },
        },
        'No attempts remaining'
      );
    }

    this.loadingScreen = false;
  }

  setupDocumentsToBeSigned(documentsToBeSigned) {
    const insurabilityDocumentSetup = {
      formType: 'Insurability',
      pdfUrl: this.controller.getViewInsuredInsurabilityFormURL(),
      textPrompt: `
      <p>As you respond to the insurability statement provided, please consider the following:  If you have been tested for COVID-19 or sought medical advice for COVID-19 symptoms (fever, cough, cold, flu–like, or shortness of breath), it qualifies as having sought or received treatment. If you have had these symptoms and called a health professional, then you have sought advice from a medical professional.  Please include this information in the disclosure of exceptions.</p>
      <br>
      <p>Finally, if you need to provide details about a change in health, including if you have been exposed to someone who tested positive for COVID-19 or if you have sought testing or medical advice, please reach out to our support team at <a href="mailto:${Product.channels.CAS.helpEmail}">${Product.channels.CAS.helpEmail}</a>
      or call us at <a href="tel:${Product.channels.CAS.phone}">${Product.channels.CAS.phone}</a> (9am-6pm Eastern Time, Monday-Friday).</p>
      `,
      signFn: async () => {
        UILoggerSingleton.getInstance().info(
          {
            module: 'InsuredEsignComponent',
            function: 'signFn',
            category: UICategory.milestone,
            context: {
              piiLevel: UIPiiLevelEnum.nonPII,
            },
          },
          'Insurability form signed'
        );
      },
    };

    const amendmentDocumentSetup = {
      formType: 'Amendments',
      pdfUrl: this.controller.getViewInsuredAmendmentsFormURL(),
      textPrompt: `
      <p>As you respond to the insurability statement provided, please consider the following:  If you have been tested for COVID-19 or sought medical advice for COVID-19 symptoms (fever, cough, cold, flu–like, or shortness of breath), it qualifies as having sought or received treatment. If you have had these symptoms and called a health professional, then you have sought advice from a medical professional.  Please include this information in the disclosure of exceptions.</p>
      <br>
      <p>Finally, if you need to provide details about a change in health, including if you have been exposed to someone who tested positive for COVID-19 or if you have sought testing or medical advice, please reach out to our support team at <a href="mailto:${Product.channels.CAS.helpEmail}">${Product.channels.CAS.helpEmail}</a>
      or call us at <a href="tel:${Product.channels.CAS.phone}">${Product.channels.CAS.phone}</a> (9am-6pm Eastern Time, Monday-Friday).</p>
      `,
      signFn: async () => {
        UILoggerSingleton.getInstance().info(
          {
            module: 'InsuredEsignComponent',
            function: 'signFn',
            category: UICategory.milestone,
            context: {
              piiLevel: UIPiiLevelEnum.nonPII,
            },
          },
          'Amendments form signed'
        );
      },
    };

    const result = [];
    if (documentsToBeSigned.includes('Insurability')) {
      result.push(insurabilityDocumentSetup);
    }

    if (documentsToBeSigned.includes('Amendment')) {
      result.push(amendmentDocumentSetup);
    }

    return result;
  }

  close(): void {
    window.close();
  }
}
